import { useEffect, useState } from 'react';

import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { IconButton, Snackbar, Typography, makeStyles } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { Alert } from '@material-ui/lab';

import { OpaPolicyClient } from './OpaPolicyClient';
import { PolicyEditForm } from './PolicyEditForm';

const useStyles = makeStyles((theme) => ({
  description: {
    marginBottom: theme.spacing(2),
  },
  notice: {
    marginBottom: theme.spacing(2),
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  editButton: {
    marginLeft: 'auto',
  },
  refreshingOverlay: {
    position: 'relative',
    opacity: 0.6,
    pointerEvents: 'none',
  },
}));

// Type definitions for the API response
interface CloudPermissions {
  // AWS properties
  accounts?: string[];
  regions?: string[];
  allowed_iam_roles?: string[];
  partitions?: string[];
  // Azure properties
  subscriptions?: string[];
  tenants?: string[];
  // GCP properties
  project_ids?: string[];
}

interface RepositoryPermissions {
  aws?: CloudPermissions;
  azure?: CloudPermissions;
  gcp?: CloudPermissions;
}

interface PoliciesResponse {
  repository: string;
  version: string;
  permissions: RepositoryPermissions;
}

interface FlattenedAccount {
  id: string; // Unique identifier for table (not displayed)
  accountName: string;
  accountId: string;
  provider: string;
  regions: string[];
}

// Permission for OPA policy updates (basic permission, not resource-scoped)
const OPA_POLICY_UPDATE_PERMISSION = {
  type: 'basic' as const,
  name: 'opa.policy.update',
  attributes: { action: 'update' as const },
};

// Define columns outside component to prevent re-rendering
const TABLE_COLUMNS: TableColumn[] = [
  { title: 'Account Name', field: 'accountName' },
  { title: 'Account ID', field: 'accountId' },
  { title: 'Cloud Provider', field: 'provider' },
  {
    title: 'Regions',
    field: 'regions',
    render: (row: any) => row.regions.join(', '),
  },
];

export const AuthorizedAccountsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<FlattenedAccount[]>([]);
  const [permissions, setPermissions] = useState<RepositoryPermissions>({});
  const [version, setVersion] = useState('v0');

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Check if user has permission to update policies
  const { allowed: canEdit } = usePermission({
    permission: OPA_POLICY_UPDATE_PERMISSION,
  });

  useEffect(() => {
    const fetchPermissions = async () => {
      setLoading(true);
      setError(null);

      try {
        const repositoryName = entity.metadata.name;

        // Get the backend base URL
        const baseUrl = await discoveryApi.getBaseUrl('proxy');

        // Call the backend proxy endpoint (appends /v0/policies to the base OPA auth URL)
        const response = await fetchApi.fetch(
          `${baseUrl}/opa-auth/v0/policies?repository=${encodeURIComponent(repositoryName)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            // Repository not found in OPA policies - no policy file exists
            setLoading(false);
            return;
          }
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data: PoliciesResponse = await response.json();

        // Store permissions and version for editing
        setPermissions(data.permissions);
        setVersion(data.version);

        // Transform the permissions data into flattened accounts
        const flattenedAccounts: FlattenedAccount[] = [];

        // Process AWS permissions
        if (data.permissions.aws) {
          const awsPerms = data.permissions.aws;
          const awsAccounts = awsPerms.accounts || [];
          const awsRegions = awsPerms.regions || [];

          awsAccounts.forEach((accountId) => {
            flattenedAccounts.push({
              id: `AWS-${accountId}`,
              accountName: `AWS-${accountId}`,
              accountId,
              provider: 'AWS',
              regions: awsRegions,
            });
          });
        }

        // Process Azure permissions
        if (data.permissions.azure) {
          const azurePerms = data.permissions.azure;
          const azureSubscriptions = azurePerms.subscriptions || [];
          const azureRegions = azurePerms.regions || [];

          azureSubscriptions.forEach((subscriptionId) => {
            flattenedAccounts.push({
              id: `Azure-${subscriptionId}`,
              accountName: `Azure-${subscriptionId}`,
              accountId: subscriptionId,
              provider: 'Azure',
              regions: azureRegions,
            });
          });
        }

        // Process GCP permissions
        if (data.permissions.gcp) {
          const gcpPerms = data.permissions.gcp;
          const gcpProjects = gcpPerms.project_ids || [];
          const gcpRegions = gcpPerms.regions || [];

          gcpProjects.forEach((projectId) => {
            flattenedAccounts.push({
              id: `GCP-${projectId}`,
              accountName: `GCP-${projectId}`,
              accountId: projectId,
              provider: 'GCP',
              regions: gcpRegions,
            });
          });
        }

        // Sort by provider, then by account ID
        flattenedAccounts.sort((a, b) => {
          const providerCompare = a.provider.localeCompare(b.provider);
          if (providerCompare !== 0) {
            return providerCompare;
          }

          // Try numeric sort for AWS account IDs
          const aNum = Number(a.accountId);
          const bNum = Number(b.accountId);

          if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
            return aNum - bNum;
          }

          return a.accountId.localeCompare(b.accountId);
        });

        setAccounts(flattenedAccounts);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch permissions');
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [entity.metadata.name, discoveryApi, fetchApi]);

  const handleEdit = () => {
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  const handleSave = async (updatedPermissions: RepositoryPermissions) => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    const client = new OpaPolicyClient(discoveryApi, fetchApi);

    try {
      // Convert RepositoryPermissions to the stricter format
      const policyPermissions = {
        aws: updatedPermissions.aws ? {
          accounts: updatedPermissions.aws.accounts || [],
          regions: updatedPermissions.aws.regions || [],
          allowed_iam_roles: updatedPermissions.aws.allowed_iam_roles,
          partitions: updatedPermissions.aws.partitions,
        } : undefined,
        azure: updatedPermissions.azure ? {
          subscriptions: updatedPermissions.azure.subscriptions || [],
          tenants: updatedPermissions.azure.tenants || [],
          regions: updatedPermissions.azure.regions || [],
        } : undefined,
        gcp: updatedPermissions.gcp ? {
          project_ids: updatedPermissions.gcp.project_ids || [],
          regions: updatedPermissions.gcp.regions || [],
        } : undefined,
      };

      await client.updatePolicy(entity.metadata.name, {
        version,
        permissions: policyPermissions,
      });

      setIsEditing(false);
      setSaving(false);
      setRefreshing(true);
      setSaveSuccess('Policy updated. Refreshing...');

      // Refresh the data
      const baseUrl = await discoveryApi.getBaseUrl('proxy');
      const response = await fetchApi.fetch(
        `${baseUrl}/opa-auth/${version}/policies?repository=${encodeURIComponent(entity.metadata.name)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data: PoliciesResponse = await response.json();
        setPermissions(data.permissions);

        // Re-flatten accounts
        const flattenedAccounts: FlattenedAccount[] = [];

        if (data.permissions.aws) {
          const awsPerms = data.permissions.aws;
          const awsAccounts = awsPerms.accounts || [];
          const awsRegions = awsPerms.regions || [];

          awsAccounts.forEach((accountId) => {
            flattenedAccounts.push({
              id: `AWS-${accountId}`,
              accountName: `AWS-${accountId}`,
              accountId,
              provider: 'AWS',
              regions: awsRegions,
            });
          });
        }

        if (data.permissions.azure) {
          const azurePerms = data.permissions.azure;
          const azureSubscriptions = azurePerms.subscriptions || [];
          const azureRegions = azurePerms.regions || [];

          azureSubscriptions.forEach((subscriptionId) => {
            flattenedAccounts.push({
              id: `Azure-${subscriptionId}`,
              accountName: `Azure-${subscriptionId}`,
              accountId: subscriptionId,
              provider: 'Azure',
              regions: azureRegions,
            });
          });
        }

        if (data.permissions.gcp) {
          const gcpPerms = data.permissions.gcp;
          const gcpProjects = gcpPerms.project_ids || [];
          const gcpRegions = gcpPerms.regions || [];

          gcpProjects.forEach((projectId) => {
            flattenedAccounts.push({
              id: `GCP-${projectId}`,
              accountName: `GCP-${projectId}`,
              accountId: projectId,
              provider: 'GCP',
              regions: gcpRegions,
            });
          });
        }

        flattenedAccounts.sort((a, b) => {
          const providerCompare = a.provider.localeCompare(b.provider);
          if (providerCompare !== 0) {
            return providerCompare;
          }

          const aNum = Number(a.accountId);
          const bNum = Number(b.accountId);

          if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
            return aNum - bNum;
          }

          return a.accountId.localeCompare(b.accountId);
        });

        setAccounts(flattenedAccounts);
        setRefreshing(false);
        setSaveSuccess('Policy updated successfully.  Please allow up to 2 minutes for changes to take effect.');
      } else {
        setRefreshing(false);
        setSaveSuccess(
          'Policy updated successfully. ' +
          'Please allow up to 2 minutes for changes to take effect. ' +
          'You may need to refresh the page to see changes.'
        );
      }

    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save policy');
      setSaving(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <InfoCard title="Authorized Cloud Accounts">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Authorized Cloud Accounts">
        <ResponseErrorPanel error={new Error(error)} />
      </InfoCard>
    );
  }

  // Render edit form if in edit mode
  if (isEditing) {
    // Convert RepositoryPermissions to stricter PolicyPermissions format
    const policyPermissions = {
      aws: permissions.aws ? {
        accounts: permissions.aws.accounts || [],
        regions: permissions.aws.regions || [],
        allowed_iam_roles: permissions.aws.allowed_iam_roles,
        partitions: permissions.aws.partitions,
      } : undefined,
      azure: permissions.azure ? {
        subscriptions: permissions.azure.subscriptions || [],
        tenants: permissions.azure.tenants || [],
        regions: permissions.azure.regions || [],
      } : undefined,
      gcp: permissions.gcp ? {
        project_ids: permissions.gcp.project_ids || [],
        regions: permissions.gcp.regions || [],
      } : undefined,
    };

    return (
      <InfoCard title="Authorized Cloud Accounts - Edit Mode">
        {saveError && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            {saveError}
          </Alert>
        )}
        <PolicyEditForm
          initialPermissions={policyPermissions}
          onSave={handleSave}
          onCancel={handleCancelEdit}
          loading={loading}
          saving={saving}
        />
      </InfoCard>
    );
  }

  return (
    <>
      <InfoCard 
        title="Authorized Cloud Accounts"
        action={
          canEdit && !error && !refreshing ? (
            <IconButton onClick={handleEdit} size="small" title="Edit policy">
              <EditIcon />
            </IconButton>
          ) : undefined
        }
      >
        {refreshing && <Progress />}
        <div className={refreshing ? classes.refreshingOverlay : undefined}>
          <Typography variant="body2" className={classes.description}>
            These are the cloud accounts to which this repository is authorized to deploy resources.
          </Typography>
          {accounts.length === 0 && (
            <Typography variant="body2" className={classes.notice}>
              No authorized cloud accounts configured for this repository.
            </Typography>
          )}
          <Table
            options={{
              search: false,
              paging: false,
              toolbar: false,
              padding: 'dense',
            }}
            data={accounts}
            columns={TABLE_COLUMNS}
          />
        </div>
      </InfoCard>

      {/* Success snackbar */}
      <Snackbar
        open={!!saveSuccess}
        autoHideDuration={6000}
        onClose={() => setSaveSuccess(null)}
      >
        <Alert onClose={() => setSaveSuccess(null)} severity="success">
          {saveSuccess}
        </Alert>
      </Snackbar>
    </>
  );
};
